// Late-afternoon tropical sky: a vertex-coloured dome (warm haze at the
// horizon, blue overhead, glow around the sun) and a sun disc. No textures or
// shaders, so it costs two draw calls.

import { Color3, DynamicTexture, Mesh, MeshBuilder, Scene, StandardMaterial, Vector3 } from './babylon';

export const HORIZON = new Color3(0.86, 0.8, 0.7);
const ZENITH = new Color3(0.3, 0.5, 0.82);
const SUN_GLOW = new Color3(1.0, 0.82, 0.55);

/** `sunDir` points from the scene towards the sun. */
export function buildSky(scene: Scene, sunDir: Vector3): Mesh[] {
  const sun = sunDir.normalizeToNew();
  const dome = MeshBuilder.CreateSphere('sky', { diameter: 3000, segments: 24, sideOrientation: Mesh.BACKSIDE }, scene);
  const pos = dome.getVerticesData('position')!;
  const colors = new Float32Array((pos.length / 3) * 4);
  for (let i = 0; i < pos.length / 3; i++) {
    const d = new Vector3(pos[i * 3]!, pos[i * 3 + 1]!, pos[i * 3 + 2]!).normalize();
    const up = Math.max(0, d.y);
    const t = Math.pow(up, 0.45);
    let r = HORIZON.r + (ZENITH.r - HORIZON.r) * t;
    let g = HORIZON.g + (ZENITH.g - HORIZON.g) * t;
    let b = HORIZON.b + (ZENITH.b - HORIZON.b) * t;
    const toSun = Math.max(0, Vector3.Dot(d, sun));
    const glow = Math.pow(toSun, 6) * 0.45 + Math.pow(toSun, 40) * 0.35;
    r += SUN_GLOW.r * glow;
    g += SUN_GLOW.g * glow;
    b += SUN_GLOW.b * glow;
    colors.set([Math.min(1, r), Math.min(1, g), Math.min(1, b), 1], i * 4);
  }
  dome.setVerticesData('color', colors);
  const mat = new StandardMaterial('sky-mat', scene);
  mat.disableLighting = true;
  mat.diffuseColor = Color3.Black();
  mat.emissiveColor = Color3.White();
  mat.specularColor = Color3.Black();
  mat.fogEnabled = false;
  dome.material = mat;
  dome.useVertexColors = true;
  dome.infiniteDistance = true;
  dome.isPickable = false;
  dome.applyFog = false;

  // Sun disc with a soft halo, always facing the camera.
  const tex = new DynamicTexture('sun-tex', { width: 128, height: 128 }, scene, true);
  const ctx = tex.getContext() as CanvasRenderingContext2D;
  const grad = ctx.createRadialGradient(64, 64, 4, 64, 64, 64);
  grad.addColorStop(0, 'rgba(255,250,235,1)');
  grad.addColorStop(0.18, 'rgba(255,240,200,1)');
  grad.addColorStop(0.3, 'rgba(255,210,140,0.45)');
  grad.addColorStop(1, 'rgba(255,190,120,0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 128, 128);
  tex.hasAlpha = true;
  tex.update(true);
  const disc = MeshBuilder.CreatePlane('sun', { size: 260 }, scene);
  disc.position = sun.scale(1300);
  disc.billboardMode = Mesh.BILLBOARDMODE_ALL;
  disc.infiniteDistance = true;
  const discMat = new StandardMaterial('sun-mat', scene);
  discMat.disableLighting = true;
  discMat.emissiveTexture = tex;
  discMat.opacityTexture = tex;
  discMat.fogEnabled = false;
  disc.material = discMat;
  disc.isPickable = false;
  disc.applyFog = false;
  return [dome, disc, buildClouds(scene, sun)];
}

/** Soft cumulus clusters, brighter on top, flat and greyer underneath. */
function cloudTexture(scene: Scene): DynamicTexture {
  const w = 2048;
  const h = 256;
  const tex = new DynamicTexture('tex-clouds', { width: w, height: h }, scene, true);
  const ctx = tex.getContext() as CanvasRenderingContext2D;
  ctx.clearRect(0, 0, w, h);
  let seed = 3;
  const rand = (): number => {
    seed = (seed * 16807) % 2147483647;
    return seed / 2147483647;
  };
  for (let c = 0; c < 16; c++) {
    const cx = (c / 16) * w + rand() * 80;
    const base = h * (0.7 + rand() * 0.15);
    const width = 90 + rand() * 160;
    for (let i = 0; i < 26; i++) {
      const x = cx + (rand() - 0.5) * width;
      const r = 12 + rand() * 26;
      const y = base - rand() * (h * 0.4) * (1 - Math.abs(x - cx) / width);
      const g = ctx.createRadialGradient(x, y, r * 0.2, x, y, r);
      const shade = Math.floor(225 + 30 * (1 - y / h));
      g.addColorStop(0, `rgba(${shade},${shade},${shade + 5},0.6)`);
      g.addColorStop(1, `rgba(${shade},${shade},${shade + 5},0)`);
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }
    // Flat, slightly grey bases.
    ctx.fillStyle = 'rgba(190,190,198,0.25)';
    ctx.fillRect(cx - width * 0.45, base - 6, width * 0.9, 8);
  }
  tex.hasAlpha = true;
  tex.wrapU = 1;
  tex.update(true);
  return tex;
}

/** A band of clouds just above the horizon, inside the sky dome (one draw call). */
function buildClouds(scene: Scene, sun: Vector3): Mesh {
  const n = 64;
  const lower: Vector3[] = [];
  const upper: Vector3[] = [];
  for (let i = 0; i <= n; i++) {
    const a = (i / n) * Math.PI * 2;
    const r = 1300;
    lower.push(new Vector3(Math.cos(a) * r, 30, Math.sin(a) * r));
    upper.push(new Vector3(Math.cos(a) * r * 0.94, 230, Math.sin(a) * r * 0.94));
  }
  const band = MeshBuilder.CreateRibbon('clouds', { pathArray: [lower, upper], sideOrientation: Mesh.DOUBLESIDE }, scene);
  const tex = cloudTexture(scene);
  tex.uScale = 4;
  const mat = new StandardMaterial('clouds-mat', scene);
  mat.disableLighting = true;
  mat.emissiveTexture = tex;
  mat.opacityTexture = tex;
  // Warm the clouds on the sun side a touch via a global tint.
  mat.emissiveColor = new Color3(1, 0.97, 0.92).scale(0.95 + 0.05 * Math.max(0, sun.y));
  mat.fogEnabled = false;
  mat.backFaceCulling = false;
  band.material = mat;
  band.infiniteDistance = true;
  band.isPickable = false;
  band.applyFog = false;
  return band;
}
